import React, { useContext, useState } from 'react';
import { ADDRESS, CREATED_ON, DELETE_PARTY_PERMISSION_MESSAGE, NO_OF_DOMAINS, PARTY_NAME, PARTY_TYPE, UPDATE_PARTY_PERMISSION_MESSAGE } from '../Utils/Constants';
import { TParty, formatDate } from '../Types/party.types';
import TableCell from 'Components/Common/components/TableCell';
import { CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import { DropdownMenu, DropdownMenuItem } from 'Pages/GMS/ISR/UI-Model/components/Common/DropdownMenu';
import { ConfirmationDialog } from 'Pages/GMS/ISR/UI-Model/components/Common/ConfirmationDialog';
import { toast } from 'react-toastify';
import { AppContext } from 'Context/AppContext';

import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type PartyTableProps = {
    parties: TParty[];
    onEditParty?: (party: TParty, index: number) => void;
    showLoader?: boolean;
    sortBy?: string;
    sortOrder?: string;
    onSortChange?: (column: string) => void;
    onPartyDeleted?: () => void;
    deletePermission: any;
    updatePermission: any;
    onNavigateToDomain: (party: TParty) => void
}

const PartyTable = ({ parties, onEditParty, showLoader = false, sortBy = 'name', sortOrder = 'asc', onSortChange, onPartyDeleted, deletePermission, updatePermission, onNavigateToDomain }: PartyTableProps) => {
    const appContext = useContext(AppContext);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; partyIndex: number | null; partyName: string; partyId: string | null;}>({ isOpen: false, partyIndex: null, partyName: '', partyId: null});
    const [isDeleting, setIsDeleting] = useState(false);

    const renderSortIcon = (column: string) => {
        if (sortBy !== column) {
            return <i className="fas fa-sort tw-text-gray-400 tw-ml-1" />;
        }
        return sortOrder === 'asc' ? 
            <i className="fas fa-sort-up tw-text-gray-700 tw-ml-1" /> : 
            <i className="fas fa-sort-down tw-text-gray-700 tw-ml-1" />;
    };

    const handleColumnClick = (column: string) => {
        if (onSortChange && !showLoader) {
            onSortChange(column);
        }
    };

    const handleCopyId = (party: TParty) => {
        navigator.clipboard.writeText(party.id).then(() => {
            toast.success('Party ID copied to clipboard!');
        }).catch(() => {
            toast.error('Failed to copy Party ID');
        });
    };

    const handleEdit = (party: TParty, index: number) => {
        if (!updatePermission) {
            toast.warn(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        if (onEditParty) {
            onEditParty(party, index);
        }
    };

    const handleDeleteClick = (party: TParty, index: number) => {
        if (!deletePermission) {
            toast.warn(DELETE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setDeleteConfirmation({
            isOpen: true,
            partyIndex: index,
            partyName: party.name,
            partyId: party.id
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.partyId) return;
        
        setIsDeleting(true);
        
        try {
            const deleteResponse = await ServiceWrapper.doGet(`${api_base_url}party/delete/${deleteConfirmation.partyId}`,null,null,AuthManager.getGmsToken(),);
            const response = deleteResponse?.data;
            if (response && response.done) {
                toast.success('Party deleted successfully!');
                await appContext.refreshHierarchy.refreshParties();
                if (onPartyDeleted) {
                    onPartyDeleted();
                }
            } else {
                const errorMessage = response?.message || 'Failed to delete party';
                toast.error(errorMessage);
                
                if (errorMessage.includes('dissassociate')) {
                    toast.info('Please remove all associated domains before deleting this party.', {
                        autoClose: 5000
                    });
                }
            }
        } catch (error) {
            console.error('Error deleting party:', error);
            toast.error('An error occurred while deleting the party. Please try again.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                isOpen: false,
                partyIndex: null,
                partyName: '',
                partyId: null
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            partyIndex: null,
            partyName: '',
            partyId: null
        });
    };

    const getDropdownMenuItems = (party: TParty, index: number): DropdownMenuItem[] => [
        
        {
            id: `copy-id-${index}`,
            label: 'Copy ID',
            className: 'tw-text-[#172B4D]',
            onClick: () => handleCopyId(party),
            icon: (
                <CopyIcon />
            )
        },{
            id: `edit-${index}`,
            label: 'Edit',
            className: 'tw-text-[#172B4D]',
            onClick: () => handleEdit(party, index),
            icon: (
                <EditIcon />
            )
        },
        {
            id: `delete-${index}`,
            label: 'Delete',
            onClick: () => handleDeleteClick(party, index),
            className: 'tw-text-[#172B4D]',
            divider: true,
            icon: (
                <DeleteIcon />
            )
        }
    ];

    const renderTableContent = () => {
        if (showLoader) {
            return (
                <tr>
                    <td colSpan={6} className="tw-text-center tw-py-8">
                        <div className="tw-inline-flex tw-items-center">
                            <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading parties...
                        </div>
                    </td>
                </tr>
            );
        }

        if (parties.length === 0) {
            return (
                <tr>
                    <td colSpan={6} className="tw-text-center tw-py-8 tw-text-gray-500">
                        No parties found
                    </td>
                </tr>
            );
        }

        return parties.map((party, index) => (
            <tr key={party.id} style={{ position: 'relative' }}>
                <TableCell id={`party-name-${index}`} rowIndex={index} onClick={() => onNavigateToDomain(party)}>{party.name}</TableCell>
                <TableCell id={`address-${index}`} rowIndex={index}>{party.address || '-'}</TableCell>
                <TableCell id={`party-type-${index}`} rowIndex={index}>{party.party_type || '-'}</TableCell>
                <TableCell id={`no-of-domains-${index}`} rowIndex={index}>{party.industry_count}</TableCell>
                <TableCell id={`created-on-${index}`} rowIndex={index}>{formatDate(party.created_at)}</TableCell>
                <TableCell id={`actions-${index}`} rowIndex={index} style={{ position: 'relative', overflow: 'visible' }}>
                    <DropdownMenu
                        items={getDropdownMenuItems(party, index)}
                        trigger={
                            <button id={`overflow-btn-${index}`} className="tw-p-1 hover:tw-bg-gray-100 tw-rounded">
                                <OverFlowIcon />
                            </button>
                        }
                        position="right"
                    />
                </TableCell>
            </tr>
        ));
    };

    return (
        <>
            <div id="party-table" className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2" style={{ overflow: 'visible' }}>
                <div id="table-scroll-container" className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto', overflowY: 'visible' }}>
                    <table id="party-data-table" className="tw-bg-[#DFE1E6] tw-w-full tw-table-fixed">
                        <thead id="party-table-header" className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]">
                            <tr>
                                <th 
                                    id="party-name-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200" 
                                    style={{ width: '20%' }}
                                    onClick={() => handleColumnClick('name')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{PARTY_NAME}</span>
                                        {renderSortIcon('name')}
                                    </div>
                                </th>
                                <th id="address-header" className="table-header-style" style={{ width: '20%' }} >{ADDRESS}</th>
                                <th 
                                    id="party-type-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200" 
                                    style={{ width: '18%' }}
                                    onClick={() => handleColumnClick('party_type')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{PARTY_TYPE}</span>
                                        {renderSortIcon('party_type')}
                                    </div>
                                </th>
                                <th 
                                    id="no-of-domains-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200" 
                                    style={{ width: '20%' }}
                                    onClick={() => handleColumnClick('industry_count')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{NO_OF_DOMAINS}</span>
                                        {renderSortIcon('industry_count')}
                                    </div>
                                </th>
                                <th 
                                    id="created-on-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200"
                                    onClick={() => handleColumnClick('created_at')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{CREATED_ON}</span>
                                        {renderSortIcon('created_at')}
                                    </div>
                                </th>
                                <th id="actions-header" className="table-header-style tw-w-[5%]"></th>
                            </tr>
                        </thead>
                        <tbody id="isr-table-body" className="tw-bg-white tw-divide-y tw-divide-[#F4F5F7]" style={{ position: 'relative' }}>
                            {renderTableContent()}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Party"
                message={`Are you sure you want to delete the party "${deleteConfirmation.partyName}"?`}
                secondaryMessage={'This action cannot be undone.'}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                isLoading={isDeleting}
            />
        </>
    );
}

export default PartyTable;